import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type SyntheticEvent,
} from 'react';

import Tooltip from '@mui/material/Tooltip';
import { UMInputNumber } from '@urbanmetrix/um-kit';

import { ApplyIcon, RefreshIcon, Target05Icon } from 'components/icons';
import type { PointerCoordinates } from './cadastralShared';

const ROTATE_CONTROL_LONG_PRESS_MS = 180;

interface RotateControlManualRotationState {
  isActive: boolean;
  pointerId: number | null;
}

interface RotateControlContentProps {
  initialAngle: number;
  canResetCenter?: boolean;
  onRotate: () => void;
  onResetCenter?: () => void;
  onAngleChange: (nextValue: number) => void;
  onManualRotateStart: (pointer: PointerCoordinates) => void;
  onManualRotateMove: (pointer: PointerCoordinates) => void;
  onManualRotateEnd: () => void;
}

const RotateControlContent = ({
  initialAngle,
  canResetCenter = false,
  onRotate,
  onResetCenter,
  onAngleChange,
  onManualRotateStart,
  onManualRotateMove,
  onManualRotateEnd,
}: RotateControlContentProps) => {
  const stopInteraction = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
  }, []);
  const [angle, setAngle] = useState(initialAngle);
  const [isExpanded, setIsExpanded] = useState(false);
  const [manualRotationState, setManualRotationState] = useState<RotateControlManualRotationState>({
    isActive: false,
    pointerId: null,
  });
  const holdTimerRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    setAngle(initialAngle);
  }, [initialAngle]);

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current != null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const stopManualRotation = useCallback(() => {
    clearHoldTimer();
    setManualRotationState((previousState) => {
      if (!previousState.isActive) {
        return previousState;
      }
      suppressClickRef.current = true;
      onManualRotateEnd();
      return {
        isActive: false,
        pointerId: null,
      };
    });
  }, [clearHoldTimer, onManualRotateEnd]);

  useEffect(() => {
    if (!manualRotationState.isActive) {
      return;
    }

    const handleWindowPointerMove = (event: PointerEvent) => {
      if (manualRotationState.pointerId != null && event.pointerId !== manualRotationState.pointerId) {
        return;
      }
      onManualRotateMove({ clientX: event.clientX, clientY: event.clientY });
    };

    const handleWindowPointerEnd = (event: PointerEvent) => {
      if (manualRotationState.pointerId != null && event.pointerId !== manualRotationState.pointerId) {
        return;
      }
      stopManualRotation();
    };

    window.addEventListener('pointermove', handleWindowPointerMove);
    window.addEventListener('pointerup', handleWindowPointerEnd);
    window.addEventListener('pointercancel', handleWindowPointerEnd);

    return () => {
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('pointerup', handleWindowPointerEnd);
      window.removeEventListener('pointercancel', handleWindowPointerEnd);
    };
  }, [manualRotationState, onManualRotateMove, stopManualRotation]);

  useEffect(
    () => () => {
      clearHoldTimer();
      if (manualRotationState.isActive) {
        onManualRotateEnd();
      }
    },
    [clearHoldTimer, manualRotationState.isActive, onManualRotateEnd]
  );

  const buildPointerCoordinates = (event: ReactPointerEvent<HTMLButtonElement>): PointerCoordinates => ({
    clientX: event.clientX,
    clientY: event.clientY,
  });

  const handleRotateButtonPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.pointerType !== 'touch' && event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
      const pointer = buildPointerCoordinates(event);
      const pointerId = event.pointerId;
      clearHoldTimer();
      holdTimerRef.current = window.setTimeout(() => {
        suppressClickRef.current = true;
        setIsExpanded(false);
        setManualRotationState({
          isActive: true,
          pointerId,
        });
        onManualRotateStart(pointer);
      }, ROTATE_CONTROL_LONG_PRESS_MS);
    },
    [clearHoldTimer, onManualRotateStart]
  );

  const handleRotateButtonPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      stopManualRotation();
    },
    [stopManualRotation]
  );

  const handleRotateButtonPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      stopManualRotation();
    },
    [stopManualRotation]
  );

  return (
    <div
      className={`grid-rotate-control ${isExpanded ? 'grid-rotate-control--expanded' : ''} ${
        canResetCenter ? 'grid-rotate-control--with-reset' : ''
      }`.trim()}
      onClick={stopInteraction}
      onDoubleClick={stopInteraction}
      onMouseDown={stopInteraction}
      onTouchStart={stopInteraction}
    >
      {canResetCenter && onResetCenter ? (
        <Tooltip title="Reset grid center">
          <button
            type="button"
            className="grid-rotate-control-button grid-rotate-control-button--standalone"
            aria-label="Reset grid center"
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              clearHoldTimer();
            }}
            onPointerUp={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onPointerCancel={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              clearHoldTimer();
              onResetCenter();
            }}
          >
            <Target05Icon style={{ width: 18, height: 18 }} />
          </button>
        </Tooltip>
      ) : null}
      <div className="grid-rotate-control-shell">
        <Tooltip title="Rotate">
          <button
            type="button"
            className="grid-rotate-control-button"
            aria-label="Rotate shape"
            aria-expanded={isExpanded}
            onPointerDown={handleRotateButtonPointerDown}
            onPointerUp={handleRotateButtonPointerUp}
            onPointerCancel={handleRotateButtonPointerCancel}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              clearHoldTimer();
              if (suppressClickRef.current) {
                suppressClickRef.current = false;
                return;
              }
              setIsExpanded((previousValue) => !previousValue);
            }}
          >
            <RefreshIcon style={{ width: 20, height: 20 }} />
          </button>
        </Tooltip>
        <div className="grid-rotate-control-input" onMouseDown={stopInteraction} onClick={stopInteraction}>
          <UMInputNumber
            label={''}
            name="rotateAngle"
            value={angle}
            width={84}
            maxErrorMessageWidth={276}
            defaultValue={90}
            onChange={(_name, value) => {
              const nextAngle = Number(value);
              const normalizedAngle = Number.isFinite(nextAngle) ? nextAngle : 90;
              setAngle(normalizedAngle);
              onAngleChange(normalizedAngle);
            }}
          />
        </div>
        <button
          type="button"
          className="grid-rotate-control-apply"
          aria-label="Rotate shape by the selected angle"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRotate();
          }}
        >
          <ApplyIcon style={{ width: 16, height: 16, color: '#ffffff' }} />
        </button>
      </div>
    </div>
  );
};

export default RotateControlContent;
